/* import __COLOCATED_TEMPLATE__ from './new-export-schedule-side-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import moment from 'moment-timezone';
import { cached } from 'tracked-toolbox';
import type { ExportRule, SchedulePreset } from 'embercom/models/reporting/dataset-export-schedule';
import type DatasetExportSchedule from 'embercom/models/reporting/dataset-export-schedule';
import { isEmpty } from 'underscore';
import type DatasetExportScheduleService from 'embercom/services/dataset-export-schedule-service';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import { ScheduleFrequencyHelpers } from 'embercom/utils/schedule-frequency-helpers';

interface Args {
  onClose: () => void;
  isOpen: boolean;
  schedule: DatasetExportSchedule;
  header: string;
}

export default class NewExportScheduleSideDrawer extends Component<Args> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare datasetExportScheduleService: DatasetExportScheduleService;
  @service notificationsService: any;

  @tracked deliveryAt: Date | undefined = this.args.schedule.deliveryAt;

  get disableSaveButton() {
    return isEmpty(this.schedule.name);
  }

  get schedule() {
    return this.args.schedule;
  }

  @action
  scheduleExport() {
    this.save();
    this.args.onClose();
  }

  async save() {
    try {
      let savedSchedule = await this.schedule.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('reporting.data-export.schedule-export.new-schedule.success-message', {
          deliveryTime: savedSchedule.formattedDeliveryAt,
        }),
      );
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('reporting.data-export.schedule-export.new-schedule.error-message'),
      );
    }
  }

  get weekDays() {
    return [
      this.intl.t('app.lib.reporting.week-days.sunday'),
      this.intl.t('app.lib.reporting.week-days.monday'),
      this.intl.t('app.lib.reporting.week-days.tuesday'),
      this.intl.t('app.lib.reporting.week-days.wednesday'),
      this.intl.t('app.lib.reporting.week-days.thursday'),
      this.intl.t('app.lib.reporting.week-days.friday'),
      this.intl.t('app.lib.reporting.week-days.saturday'),
    ];
  }

  get currentDayOfWeekLabel() {
    return this.weekDays[this.currentDayOfWeek];
  }

  get selectedTime() {
    return this.schedule.exportRule.deliveryTime.hour;
  }

  @cached
  get hours() {
    return ScheduleFrequencyHelpers.getHours(this.timezone, moment);
  }

  @action
  onTimeSelectionChange(timeIndex: number) {
    this.schedule.exportRule = {
      ...this.schedule.exportRule,
      deliveryTime: {
        hour: timeIndex,
        minute: 0,
        timezone: this.timezone,
      },
    };
  }

  get currentDayOfWeek() {
    return new Date().getDay();
  }

  get timezone() {
    return this.schedule.timezone;
  }

  presetExportRule(preset: SchedulePreset): ExportRule {
    return this.datasetExportScheduleService.exportRuleFrom(preset, this.timezone);
  }

  @action
  updateSelection(preset: SchedulePreset) {
    this.schedule.updateExportRule(this.presetExportRule(preset));
    this.deliveryAt = undefined;
  }

  get saveButtonText() {
    // @ts-ignore
    if (this.schedule.isNew) {
      return this.intl.t('reporting.data-export.schedule-export.new-schedule.schedule-button');
    } else {
      return this.intl.t('reporting.data-export.schedule-export.update-schedule-button');
    }
  }

  get selectedPresetLabel() {
    return this.datasetExportScheduleService.customLabel(this.schedule);
  }

  get frequencyItems() {
    return ScheduleFrequencyHelpers.getFrequencyPresetItems(
      this.intl,
      'reporting.data-export.schedule-export.new-schedule',
      this.schedule.exportRule,
      this.timezone,
      this.datasetExportScheduleService,
      (preset) => this.updateSelection(preset),
    );
  }

  get isCustomSelected() {
    return ScheduleFrequencyHelpers.isCustomFrequencySelected(this.frequencyItems);
  }

  @action
  handleApplyCustomFrequency(rule: ExportRule) {
    this.schedule.exportRule = rule;
  }

  @action
  onClose() {
    if (this.schedule.get('isNew')) {
      this.schedule.deleteRecord();
      this.deliveryAt = undefined;
    } else {
      this.schedule.rollbackAttributes();
      this.deliveryAt = this.schedule.deliveryAt; // this is important because of flaky behavior https://github.com/intercom/intercom/issues/401000#issuecomment-2875956450
    }
    this.args.onClose();
  }

  get formattedDeliveryAt() {
    return this.schedule.formatDeliveryAt(this.deliveryAt);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::NewExportScheduleSideDrawer': typeof NewExportScheduleSideDrawer;
    'reporting/new-export-schedule-side-drawer': typeof NewExportScheduleSideDrawer;
  }
}
